import api from 'axios';
import { API_BASE_URL, callApi } from 'utils/ApiUtil';

/** 챗봇 통합 엔드포인트 (일반질의 / 매트릭스 선선택 / 턴중단 공용) */
const CHAT_URL = `${API_BASE_URL}/v1/chat`;

/** 대화유형코드 (chat_type) */
const CHAT_TYPE_CHAT = 'chat';
const CHAT_TYPE_CANCEL = 'cancel';

/** 스코프유형코드 (scope_type) */
const SCOPE_TYPE_MATRIX_LIST = 'matrix_list';

const CHATBOT_URL = `${API_BASE_URL}/v1/chatbot/stream`;

export interface SelectListItem {
  utmId: string;
  utmNo: string;
  bildPlntCd: string;
  bildPlntCdNm: string;
  utmNm: string;
  verNo: string;
  utmWkProgStatCdNm: string;
}

export interface SelectListData {
  intent: string;
  assistant_message: string;
  items?: SelectListItem[];
  matrix_list?: string[];
}

export interface ChatStreamCallbacks {
  onChunk: (chunk: string) => void;
  onDone: () => void;
  onError: (error: Error) => void;
  onUiAction?: (action: string) => void;
  onSelectList?: (data: SelectListData) => void;
}

export async function fetchSession(sessionTpCd: string): Promise<string> {
  const res = await api.post(`${API_BASE_URL}/v1/cmn-session`, { sessionTpCd });
  return res.data.sessionId;
}

export async function closeSession(sessionId: string): Promise<void> {
  await api.post(`${API_BASE_URL}/v1/cmn-session/close`, { sessionId });
}

/** 진행 중인 답변 생성 중단 요청 (BE가 AI 서버로 session_id 변환 후 전달) */
export async function cancelChat(sessionId: string): Promise<void> {
  await api.post(CHAT_URL, { chatType: CHAT_TYPE_CANCEL, sessionId });
}

export async function sendMatrixMaster(sessionId: string): Promise<void> {
  await api.post(`${API_BASE_URL}/v1/ut/ai-ref/send-master`, { sessionId });
}

export interface MatrixScope {
  utmId: string;
  verNo: string;
  bildPlntCd: string;
}

export function streamChat(
  question: string,
  sessionId: string | null,
  callbacks: ChatStreamCallbacks,
  matrixScope?: MatrixScope
): AbortController {
  const controller = new AbortController();

  const body: Record<string, unknown> = { chatType: CHAT_TYPE_CHAT, question, sessionId };
  if (matrixScope) {
    // 매트릭스 선선택 질의: scope_ctn 은 배열 규격
    body.scopeType = SCOPE_TYPE_MATRIX_LIST;
    body.scopeCtn = [matrixScope];
  }
  console.log('[Chatbot] streamChat body:', JSON.stringify(body));

  fetch(CHAT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');

      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let done = false;
      let currentEvent = '';

      while (!done) {
        const result = await reader.read();
        done = result.done;
        if (done) break;
        const value = result.value;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            let data = line.slice(5).trim();
            if (!data) continue;

            // error 이벤트 처리
            if (currentEvent === 'error') {
              try {
                const parsed = JSON.parse(data);
                callbacks.onError(new Error(parsed.message || '알 수 없는 오류'));
              } catch {
                callbacks.onError(new Error(data));
              }
              currentEvent = '';
              return;
            }

            // done 이벤트 처리
            if (currentEvent === 'done') {
              currentEvent = '';
              continue;
            }

            // raw 이벤트 무시 (네트워크 탭 확인용)
            if (currentEvent === 'raw') {
              currentEvent = '';
              continue;
            }

            // ui_action 이벤트 처리
            if (currentEvent === 'ui_action') {
              callbacks.onUiAction?.(data);
              currentEvent = '';
              continue;
            }

            // select_list 이벤트 처리
            if (currentEvent === 'select_list') {
              try {
                const parsed = JSON.parse(data);
                callbacks.onSelectList?.(parsed);
              } catch {
                // 파싱 실패 시 무시
              }
              currentEvent = '';
              continue;
            }

            // 일반 data 청크
            // SseEmitter가 JSON 문자열로 감싸서 보내는 경우 ("\"텍스트\"") 언래핑
            if (data.startsWith('"') && data.endsWith('"')) {
              try {
                data = JSON.parse(data);
              } catch {
                // 파싱 실패시 원본 사용
              }
            }
            callbacks.onChunk(data);
            currentEvent = '';
          }
        }
      }

      callbacks.onDone();
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        callbacks.onError(err);
      }
    });

  return controller;
}
